import { camelize } from 'casing'
import {
  actionTypeWrapper,
  API,
  errorLogger,
  identityServiceUrl,
  LoadingTypes,
  RequestFailureMessage,
  startLoading,
  stopLoading,
  updateAppAlert,
} from '@labrav/common'
import type { Dispatch } from 'redux'
import type { PartnerType } from '../../../types/external'
import type { RootState } from '../../../store/types'

const PARTNER_IQ_FEATURE = 'PARTNERIQ'
const PARTNER_IQ_ADMIN_FEATURE = 'PARTNER_IQ'

const normalizeSalesFeatureKey = (key: string): string => {
  const upper = key.trim().toUpperCase()
  if (upper === 'PARTNER_IQ' || upper === 'PARTNERIQ') {
    return PARTNER_IQ_FEATURE
  }
  return key
}

const normalizeSalesFeatures = (features: string[] = []): string[] =>
  Array.from(new Set(features.map(normalizeSalesFeatureKey)))

const hasPartnerIqFeature = (features: string[] = []): boolean =>
  normalizeSalesFeatures(features).includes(PARTNER_IQ_FEATURE)

type SalesSubscription = {
  features?: string[]
  partnerIqEnabled?: boolean
  partnerIqOnboardingComplete?: boolean
  [key: string]: unknown
}

const normalizeSalesSubscription = (
  sales?: SalesSubscription
): SalesSubscription | undefined => {
  if (!sales) return sales

  const normalizedFeatures = normalizeSalesFeatures(sales.features ?? [])
  const partnerIqFromFeatures = hasPartnerIqFeature(normalizedFeatures)
  const partnerIqEnabled =
    sales.partnerIqEnabled === false
      ? false
      : sales.partnerIqEnabled === true || partnerIqFromFeatures

  const featuresForUi = Array.from(
    new Set(
      normalizedFeatures.map(f =>
        f === PARTNER_IQ_FEATURE ? PARTNER_IQ_ADMIN_FEATURE : f
      )
    )
  )

  return {
    ...sales,
    features: featuresForUi,
    partnerIqEnabled,
    partnerIqOnboardingComplete:
      sales.partnerIqOnboardingComplete === true ? true : undefined,
  }
}

const normalizeSubscribedProducts = <
  T extends { SALES?: SalesSubscription; [key: string]: unknown },
>(
  subscribedProducts?: T
): T | undefined => {
  if (!subscribedProducts?.SALES) return subscribedProducts
  return {
    ...subscribedProducts,
    SALES: normalizeSalesSubscription(subscribedProducts.SALES),
  }
}

const patchPartnerIdentityService = (
  partnerId: string,
  data: Record<string, unknown>
) => API.patch(`${identityServiceUrl}/partners/${partnerId}`, data)

const setAccount = (data: Record<string, unknown>) => {
  const camelized = camelize(data) as Record<string, unknown> & {
    subscribedProducts?: { SALES?: SalesSubscription; [key: string]: unknown }
  }
  return {
    type: 'SET_PARTNER',
    payload: {
      ...camelized,
      subscribedProducts: normalizeSubscribedProducts(
        camelized.subscribedProducts
      ),
    },
  }
}

export const updateAccount =
  (
    partnerId: string,
    partnerObjectType: PartnerType,
    dataPartner: Record<string, unknown>,
    infoData: Record<string, string>,
    handleNext?: () => void
  ) =>
  async (dispatch: Dispatch, getState: () => RootState) => {
    dispatch(startLoading(LoadingTypes.GENERAL))
    const partnerData =
      getState().AccountData[partnerObjectType]?.partnerData || {}
    try {
      const res = await patchPartnerIdentityService(partnerId, dataPartner)
      if (handleNext) {
        handleNext()
      }
      await dispatch(
        actionTypeWrapper(
          partnerObjectType,
          setAccount({
            ...partnerData,
            ...res.data,
            partnerOwner: infoData,
          })
        )
      )
    } catch (err) {
      dispatch(
        updateAppAlert({
          message: RequestFailureMessage,
          messageType: 'ERROR',
          autoClose: true,
        })
      )
      const globalState = getState()
      errorLogger({ globalState })(err as Error)
      dispatch(stopLoading(LoadingTypes.GENERAL))
      return err
    } finally {
      dispatch(stopLoading(LoadingTypes.GENERAL))
    }
  }
